import { PlusIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  type DefaultView,
  SavedViewsList,
} from "@/components/news-feed/saved-views-selector/SavedViewsList";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import InlineSearchEditor from "@/components/ui/tiptap-editor/InlineSearchEditor";
import { ExpandedSearchEditor } from "@/components/ui/tiptap-editor/TiptapEditor";
import type { SavedViewItem } from "@/data-access/saved-views/queries/saved-views-queries";
import {
  getKeyMessageFilterExclusionFromFlatSavedView,
  type SavedViewFilter,
  savedViewFilterToSearchParams,
} from "@/lib/utils/filters";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";

const DEFAULT_FILTER_DAYS = 30;

const ArticlesTabContentManagementPanel = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const { feedId } = useParams({ strict: false });
  const navigate = useNavigate();

  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalCount,
  );
  const isFetchingArticles = useDiscoveryContentManagementStore(
    (state) => state.isFetchingArticles,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.search.searchTerm,
  );
  const setSearchTerm = useDiscoveryContentManagementStore(
    (state) => state.setSearchTerm,
  );
  const resetState = useDiscoveryContentManagementStore(
    (state) => state.resetState,
  );
  const setIsAddArticleModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsAddArticleModalOpen,
  );
  const isAddingArticle = useDiscoveryContentManagementStore(
    (state) => state.isAddingArticle,
  );
  const updateKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.updateSelectedKeyMessageFilterExclusion,
  );

  const handleViewSelect = (view: SavedViewItem | DefaultView) => {
    if (view.id === null) {
      updateKeyMessageFilterExclusion("OR");
      // Default view: reset to defaults
      navigate({
        to: "/discovery/$feedId/articles",
        params: { feedId: feedId ?? "" },
        search: {
          filterDays: DEFAULT_FILTER_DAYS,
          period: "day",
          selected: { CATEGORY: ["NEWS"] },
          savedViewId: undefined,
        },
      });
    } else if ("feedArticleFilter" in view) {
      updateKeyMessageFilterExclusion(
        getKeyMessageFilterExclusionFromFlatSavedView(view.feedArticleFilter),
      );
      // Apply saved view filters
      const searchParams = savedViewFilterToSearchParams(
        view.feedArticleFilter as SavedViewFilter,
      );
      navigate({
        to: "/discovery/$feedId/articles",
        params: { feedId: feedId ?? "" },
        search: {
          filterDays: searchParams.filterDays ?? DEFAULT_FILTER_DAYS,
          startDate: searchParams.startDate,
          endDate: searchParams.endDate,
          period: "day",
          selected: searchParams.selected,
          savedViewId: view.id,
        },
      });
    }
  };

  const handleSearch = (searchValue: string) => {
    resetState();
    setSearchTerm(searchValue);
  };

  const handleExpand = () => setIsSearchExpanded(true);
  const handleCollapse = () => setIsSearchExpanded(false);

  return (
    <div className="space-y-2 pb-1 pt-6">
      <div className="flex flex-col items-start sm:flex-row sm:justify-between sm:items-center">
        <SavedViewsList onViewSelect={handleViewSelect} />
        {!isSearchExpanded && (
          <div className="w-full min-w-0 sm:w-auto">
            <InlineSearchEditor
              handleSearch={handleSearch}
              handleExpand={handleExpand}
              content={searchTerm}
              isSearching={isFetchingArticles && !!searchTerm}
            />
          </div>
        )}
      </div>
      {isSearchExpanded && (
        <ExpandedSearchEditor
          handleSearch={handleSearch}
          handleExpand={handleCollapse}
          content={searchTerm}
          isSearching={isFetchingArticles && !!searchTerm}
        />
      )}
      <div className="flex items-center justify-between gap-2 px-1.5 pt-7">
        <span className="text-xs text-gray-900">
          {isFetchingArticles ? (
            <Skeleton variant="text" className="h-4 w-16" />
          ) : (
            <>
              {addCommasToNumbers(totalCount)}{" "}
              {totalCount === 1 ? "Article" : "Articles"}
            </>
          )}
        </span>

        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 h-8"
          isDisabled={isAddingArticle}
          onPress={() => setIsAddArticleModalOpen(true)}
        >
          <PlusIcon className="text-gray-600" size={14} />
          <span className="ml-1.5">Add article</span>
        </Button>
      </div>
    </div>
  );
};

export default ArticlesTabContentManagementPanel;
